import { IProductVersions } from '../../models/ProductVersions';
import { IProductDetails } from '../../models/ProductDetails';
import { IAwsFileMetadata } from '../models/IAwsFileMetadata';
import { IInstaller } from '../../models/Installer';
import { IVersion } from '../../models/Version';
import { productReleaseResources } from '../builders/productReleaseResources';
import { getUseResource } from '../resource';

interface IReleasesResponse {
  releases: [] | [IProductVersions];
}

export interface IProductReleaseApiData {
  changelogNotes: string;
  forceUpdate: boolean;
  pinCode: number;
}

export interface ICreateVersionPayload extends IProductReleaseApiData {
  filePath?: string;
  marketingNotes: string;
  emailOption: 'none' | 'allAdminsUsers' | 'notArchived' | 'activeLicense' | 'custom';
  customEmails: string;
}

interface IAdminReleaseInstaller {
  id: string;
  productId: string;
  versionGroup: string;
  version: string;
  build: number | null;
  changelog: string | null;
  forceUpdate: boolean;
  filePath: string | null;
  author: string | null;
  released: number | null;
  file?: IAwsFileMetadata;
  createdAt: string;
  updatedAt: string;
}

interface IAdminReleaseVersionGroup {
  version: string;
  installer: IAdminReleaseInstaller[];
}

interface IAdminReleaseProductInfo {
  id: string;
  legacyId: number;
  name: string;
  stripeProductId: string;
  docsPath: string;
  projectPath: string;
  created: string;
  updated: string;
}

interface IAdminProductReleases {
  product: IAdminReleaseProductInfo;
  versions: IAdminReleaseVersionGroup[];
}

interface IAdminReleasesResponse {
  status: number;
  message: string;
  data: {
    page: number;
    total_items: number;
    data: IAdminProductReleases[];
    page_size: number;
    total_pages: number;
  };
}

interface IAdminNewReleaseResponse {
  status: number;
  message: string;
  data: {
    product: IAdminReleaseProductInfo;
    files: IAwsFileMetadata[];
  };
}

const mapInstaller = (release: IAdminReleaseInstaller): IInstaller & { file?: IAwsFileMetadata } => ({
  filePath: release.filePath ?? '',
  author: (release.author ?? '') as IInstaller['author'],
  released: release.released ?? 0,
  build: release.build ?? undefined,
  changelog: release.changelog ?? '',
  forceUpdate: release.forceUpdate as IInstaller['forceUpdate'],
  version: release.version,
  file: release.file,
});

const mapVersionGroup = (group: IAdminReleaseVersionGroup): IVersion => ({
  version: group.version,
  installer: group.installer.map(mapInstaller),
});

const mapProductReleases = (item: IAdminProductReleases): IProductVersions => ({
  product: {
    id: 0,
    name: item.product.name,
    plans: [],
    created: new Date(item.product.created).getTime(),
    updated: new Date(item.product.updated).getTime(),
    stripeProductId: item.product.stripeProductId,
  },
  versions: item.versions.map(mapVersionGroup),
});

const mapGetReleasesResponse = (body: IAdminReleasesResponse): IReleasesResponse => {
  const items = body.data?.data ?? [];
  if (items.length === 0) {
    return { releases: [] };
  }
  return { releases: [mapProductReleases(items[0])] };
};

const getReleasesQueryParams = () => ({ page: 1, page_size: 1000 });

export const apiProductRelease = {
  useGetProductReleases: getUseResource(
    productReleaseResources.getProductReleases,
    mapGetReleasesResponse,
    getReleasesQueryParams
  ),
  useUpdateProductRelease: getUseResource(
    productReleaseResources.updateProductReleaseVersion,
    () => null,
    (params, payload: IProductReleaseApiData) => payload,
    void 0,
    void 0,
    { signOutUnauthorized: false }
  ),
  useGetProductInstallers: getUseResource(
    productReleaseResources.getProductInstallers,
    (body: IAdminNewReleaseResponse): { product: IProductDetails; files: IAwsFileMetadata[] } => ({
      product: {
        id: 0,
        name: body.data.product.name,
        plans: [],
        created: new Date(body.data.product.created).getTime(),
        updated: new Date(body.data.product.updated).getTime(),
        stripeProductId: body.data.product.stripeProductId,
      },
      files: body.data.files ?? [],
    })
  ),
  useCreateProductRelease: getUseResource(
    productReleaseResources.createProductReleaseVersion,
    () => null,
    (params, payload: ICreateVersionPayload) => payload,
    void 0,
    void 0,
    { signOutUnauthorized: false }
  ),
  useDeleteProductRelease: getUseResource(
    productReleaseResources.deleteProductReleaseVersion,
    () => null,
    (params, pinCode: number) => ({ pinCode }),
    void 0,
    void 0,
    { signOutUnauthorized: false }
  ),
};
